
import React, { useRef, useEffect, useState } from 'react';

interface MapPinSelectorProps {
  lat: number;
  lon: number;
  onChange: (lat: number, lon: number) => void;
  height?: string;
  pinColor?: string; // color dinámico para el pin
}

const MapPinSelector: React.FC<MapPinSelectorProps> = ({ lat, lon, onChange, height = '300px' }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletRef = useRef<any>(null);
  const mapInstanceRef = useRef<any>(null);
  const isUpdatingFromMap = useRef(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [pinColor, setPinColor] = useState('#f59e42');

  // Obtener el color del pin desde la configuración de apariencia (CSS variable --primary)
  useEffect(() => {
    const updatePinColor = () => {
      if (typeof window !== 'undefined') {
        const computedStyle = getComputedStyle(document.documentElement);
        const primaryColor = computedStyle.getPropertyValue('--primary')?.trim();
        if (primaryColor) {
          // Convertir HSL a hex si es necesario, o usar directamente si es hex
          setPinColor(primaryColor.startsWith('#') ? primaryColor : `hsl(${primaryColor})`);
        }
      }
    };

    updatePinColor();

    // Observar cambios en el atributo style del root para reaccionar a cambios de apariencia
    const observer = new MutationObserver(updatePinColor);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !mapRef.current) return;
    (async () => {
      if (!leafletRef.current) {
        leafletRef.current = await import('leaflet');
        await import('leaflet/dist/leaflet.css');
      }
      const L = leafletRef.current;
      
      if (!mapInstanceRef.current) {
        const map = L.map(mapRef.current, {
          dragging: !L.Browser.mobile,
          tap: !L.Browser.mobile,
          scrollWheelZoom: false,
          touchZoom: true
        }).setView([lat, lon], 15);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);
        
        // Manejo de drag con un dedo en móvil para mostrar el overlay
        if (L.Browser.mobile) {
          const container = mapRef.current;
          
          container.addEventListener('touchstart', (e: TouchEvent) => {
            if (e.touches.length === 1) {
              setShowOverlay(true);
            } else {
              setShowOverlay(false);
              map.dragging.enable();
            }
          }, { passive: true });

          container.addEventListener('touchend', () => {
            setShowOverlay(false);
            map.dragging.disable();
          }, { passive: true });
        }

        // Evento cuando el mapa termina de moverse
        map.on('moveend', function () {
          const center = map.getCenter();
          isUpdatingFromMap.current = true;
          onChange(center.lat, center.lng);
          setTimeout(() => {
            isUpdatingFromMap.current = false;
          }, 100);
        });
        
        mapInstanceRef.current = map;
      } else {
        // Solo actualizar si el cambio viene de fuera (no del propio mapa)
        if (!isUpdatingFromMap.current) {
          const map = mapInstanceRef.current;
          const currentCenter = map.getCenter();
          const distance = Math.abs(currentCenter.lat - lat) + Math.abs(currentCenter.lng - lon);
          // Solo actualizar si hay una diferencia significativa (evitar loops)
          if (distance > 0.0001) {
            map.panTo([lat, lon]);
          }
        }
      }
    })();
    // eslint-disable-next-line
  }, [lat, lon]);

  return (
    <div style={{ position: 'relative', width: '100%', height, touchAction: 'pan-y' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      
      {/* Overlay para móvil */}
      {showOverlay && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          pointerEvents: 'none',
          color: 'white',
          textAlign: 'center',
          padding: '20px',
          fontSize: '14px',
          fontWeight: 'bold',
          transition: 'opacity 0.3s'
        }}>
          Usa dos dedos para mover el mapa
        </div>
      )}

      {/* Pin fijo en el centro */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -100%)',
        pointerEvents: 'none',
        zIndex: 1000,
      }}>
        <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke={pinColor} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0'/>
          <circle cx='12' cy='10' r='3'/>
        </svg>
      </div>
    </div>
  );
};

export default MapPinSelector;
